import React from 'react'
import { Chip, Typography, Unstable_Grid2 as Grid, Box } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { FormattedMessage, FormattedNumber } from 'react-intl'
import HandleMarkup from '@src/components/HandleMarkup'

const useStyles = makeStyles()((theme) => ({
  loaderContainer: {
    margin: theme.spacing(2, 0, 5, 0),
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(1, 0, 3, 0),
    },
  },
  chipContainer: {
    display: 'inline-block',
    margin: theme.spacing(2, 2, 5, 0),
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      margin: theme.spacing(1, 0, 3, 0),
      overflowX: 'scroll',
    },
  },
  chip: {
    border: `1px solid ${theme.palette.grey[500]}`,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.primary.main,
    margin: theme.spacing(0, 2, 2, 0),
    minWidth: 88,
    padding: theme.spacing(6, 0),
    textAlign: 'center',
    '& .MuiChip-label': {
      padding: theme.spacing(0, 2),
    },
  },
  packSize: {
    fontSize: theme.typography.pxToRem(16),
    lineHeight: theme.typography.pxToRem(24),
    fontWeight: theme.typography.fontWeightBold,
  },
  price: {
    fontSize: theme.typography.pxToRem(12),
    fontWeight: theme.typography.fontWeightRegular,
    color: theme.palette.common.black,
    [theme.breakpoints.down('sm')]: {
      fontSize: theme.typography.pxToRem(12),
      '& .MuiTypography-body2': {
        fontSize: theme.typography.pxToRem(12),
        lineHeight: 1.2,
      },
    },
  },
  chipSelected: {
    backgroundColor: '#e7f3fb', // TODO: Find theme color
    color: theme.palette.common.black,
    borderColor: theme.palette.common.black,
    '&&:focus': {
      backgroundColor: '#e7f3fb', // TODO: Find theme color
    },
  },
  chipUnavailable: {
    backgroundColor: theme.palette.grey[200],
    borderColor: theme.palette.grey[300],
    color: theme.palette.common.black,
  },
  chipSelectedUnavailable: {
    borderColor: theme.palette.primary.main,
  },
  loader: {
    backgroundColor: theme.palette.grey[400],
    borderRadius: 5,
    height: 50,
    width: 88,
    [theme.breakpoints.down('sm')]: {
      width: 100,
    },
  },
}))

interface IChip {
  currency: string
  packSize: string | null | undefined
  price: number
  value: string
  hidePriceMessageKey?: string | null | undefined
  availabilities?: {
    displayInquireLink?: boolean | null | undefined
  }[]
}

interface ViewProductOptionsSelectableChipsProps {
  items: IChip[]
  loading?: boolean
  onSelect: (item: string) => void
  selectedChip: string
  unavailableChips?: string[]
  loadCount?: number
}

const ViewProductOptionsSelectableChips = ({
  items,
  loading,
  onSelect,
  selectedChip,
  unavailableChips,
  loadCount = 6,
}: ViewProductOptionsSelectableChipsProps) => {
  const { classes, cx } = useStyles()
  return loading ? (
    <Grid container spacing={2} className={classes.loaderContainer}>
      {Array(loadCount)
        .fill({})
        .map((_, index) => (
          <Grid xs={6} sm={4} md={3} lg={2} key={index}>
            <Box
              className={classes.loader}
              style={{ animationDelay: `${index * 0.1}s` }}
            />
          </Grid>
        ))}
    </Grid>
  ) : (
    <div className={classes.chipContainer}>
      {items.map((item, i) => {
        const isSelected = item?.value === selectedChip
        const isUnavailable = unavailableChips?.includes(item?.value)
        const hasInquireLink = item?.availabilities?.some(
          (availability) => availability?.displayInquireLink
        )

        const formattedLabel = (
          <>
            <HandleMarkup
              className={classes.packSize}
              value={item?.packSize?.toUpperCase()}
            />

            {!item?.hidePriceMessageKey ? (
              <div className={classes.price}>
                {hasInquireLink ? (
                  <Typography variant="body2">
                    <FormattedMessage id="INQUIRE" defaultMessage="Inquire" />
                  </Typography>
                ) : (
                  <Typography variant="body2">
                    <FormattedNumber
                      currency={item?.currency}
                      currencyDisplay="symbol"
                      style="currency"
                      value={item?.price}
                    />
                  </Typography>
                )}
              </div>
            ) : null}
          </>
        )

        return (
          <Chip
            key={`${i}-${item?.packSize}-${item?.value}`}
            label={formattedLabel}
            variant="outlined"
            clickable
            onClick={() => onSelect(item?.value)}
            className={cx(classes.chip, {
              [classes.chipSelected]: isSelected,
              [classes.chipUnavailable]: isUnavailable,
              [classes.chipSelectedUnavailable]: isSelected && isUnavailable,
            })}
          />
        )
      })}
    </div>
  )
}

export default ViewProductOptionsSelectableChips
